import { isValidDomainHost } from '../pages/home/homeSearchUtils';
import { getApiBaseUrl } from '../config/apiBase';

const DNS_CACHE_MS = 60_000;
const DNS_FETCH_TIMEOUT_MS = 5_000;

/** @type {Map<string, { resolved: boolean, ts: number }>} */
const dnsCache = new Map();

function readDnsCache(host) {
  const row = dnsCache.get(host);
  if (!row) return null;
  if (Date.now() - row.ts > DNS_CACHE_MS) {
    dnsCache.delete(host);
    return null;
  }
  return row.resolved;
}

function writeDnsCache(host, resolved) {
  dnsCache.set(host, { resolved: Boolean(resolved), ts: Date.now() });
}

/**
 * Resolve whether a domain exists via backend DNS check.
 * Fail-open on network/API errors so flaky infra does not block scans.
 *
 * @param {string} domain
 * @param {{ skipCache?: boolean }} [opts]
 * @returns {Promise<boolean>}
 */
export async function checkDomainNS(domain, opts = {}) {
  const host = String(domain || '').trim().toLowerCase();
  if (!isValidDomainHost(host)) return false;

  if (!opts.skipCache) {
    const cached = readDnsCache(host);
    if (cached !== null) return cached;
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), DNS_FETCH_TIMEOUT_MS);

  try {
    const base = getApiBaseUrl();
    const url = `${base}/dns/resolve?domain=${encodeURIComponent(host)}`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (!res.ok) {
      writeDnsCache(host, true);
      return true;
    }
    const data = await res.json().catch(() => null);
    if (!data || typeof data.resolved !== 'boolean') {
      writeDnsCache(host, true);
      return true;
    }
    writeDnsCache(host, data.resolved);
    return data.resolved;
  } catch {
    writeDnsCache(host, true);
    return true;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Extract mailbox domain from an email address.
 * @param {string} email
 * @returns {string}
 */
export function emailDomainFromAddress(email) {
  const raw = String(email || '').trim().toLowerCase();
  const at = raw.lastIndexOf('@');
  if (at < 0) return '';
  return raw.slice(at + 1).replace(/^\[|\]$/g, '').trim();
}
